import { Link } from "react-router-dom";
import { BookOpen, Clock, ChevronRight, Tag } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from '../components/SEO';

const SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'Blog',
  'name': 'Mobbing-Ratgeber — mobbing-melden.de',
  'description': 'Ratgeber und Hilfe-Artikel zu Mobbing, Cybermobbing, Mobbing am Arbeitsplatz und Mobbing in der Schule.',
  'url': 'https://www.mobbing-melden.de/blog',
  'publisher': { '@type': 'Organization', 'name': 'mobbing-melden.de' },
  'inLanguage': 'de-DE',
};

const FEATURED = {
  title: "Mobbing am Arbeitsplatz: Was Sie jetzt konkret tun können",
  excerpt: "Mobbing am Arbeitsplatz ist kein Kavaliersdelikt. Dieser Ratgeber zeigt, welche Schritte sofort helfen — von der Dokumentation bis zur Beschwerde beim Betriebsrat.",
  path: "/blog/mobbing-am-arbeitsplatz",
  category: "Arbeitsplatz",
  color: "#A0E8AF",
  readTime: "5 Min",
  emoji: "👔",
};

const ARTICLES = [
  {
    title: "Mobbing Protokoll richtig führen: Checkliste & Vorlage",
    excerpt: "Ein lückenloses Mobbing-Protokoll ist vor Gericht und beim Betriebsrat entscheidend. Wir zeigen, was rein muss — kostenlose Vorlage inklusive.",
    path: "/blog/mobbing-protokoll",
    category: "Dokumentation",
    color: "#BDE0FE",
    readTime: "4 Min",
    emoji: "📋",
  },
  {
    title: "Cybermobbing in der Schule: Ratgeber für Eltern 2025",
    excerpt: "Ihr Kind wird online gemobbt? Hier erfahren Sie, wie Sie Beweise sichern, welche Plattformen zuständig sind und wann die Polizei eingeschaltet werden sollte.",
    path: "/cybermobbing",
    category: "Schule",
    color: "#FFC4D6",
    readTime: "6 Min",
    emoji: "🎒",
  },
  {
    title: "Mobbing und der Betriebsrat: Rechte, Pflichten und erste Schritte",
    excerpt: "Der Betriebsrat hat konkrete Pflichten bei Mobbing-Fällen. Was er tun muss, was Sie verlangen können — und was wenn er nicht reagiert.",
    path: "/was-passiert",
    category: "Arbeitsrecht",
    color: "#F6E05E",
    readTime: "5 Min",
    emoji: "⚖️",
  },
  {
    title: "Mobbing anonym melden: So geht es, das sind deine Rechte",
    excerpt: "Du musst deinen Namen nicht nennen. Wir erklären, wie anonyme Meldungen funktionieren, was damit passiert und warum Anonymität oft besser schützt.",
    path: "/melden",
    category: "Allgemein",
    color: "#A0E8AF",
    readTime: "3 Min",
    emoji: "🔒",
  },
  {
    title: "Was ist Mobbing? Definition, Formen und Abgrenzung",
    excerpt: "Nicht jeder Konflikt ist Mobbing. Was den Unterschied ausmacht, welche Formen es gibt und wann aus Streit Mobbing wird.",
    path: "/faq",
    category: "Grundlagen",
    color: "#BDE0FE",
    readTime: "4 Min",
    emoji: "❓",
  },
  {
    title: "Mobbing in der Schule melden: Schritt-für-Schritt-Anleitung",
    excerpt: "An wen wendet man sich? Was muss dokumentiert werden? Dieser Leitfaden führt Schüler und Eltern durch den Meldeprozess.",
    path: "/eltern",
    category: "Schule",
    color: "#FFC4D6",
    readTime: "5 Min",
    emoji: "🏫",
  },
];

const CATEGORIES = ["Alle", "Arbeitsplatz", "Schule", "Dokumentation", "Arbeitsrecht", "Grundlagen", "Allgemein"];

export default function Blog() {
  return (
    <div className="min-h-screen bg-[#FFFDF9]">
      <Header />
      <SEO
        title="Mobbing Ratgeber & Blog: Hilfe bei Mobbing & Cybermobbing"
        description="Ratgeber zu Mobbing am Arbeitsplatz, Cybermobbing in der Schule, Mobbing Protokoll und Beweise sichern. Kostenlose Hilfe-Artikel von mobbing-melden.de."
        path="/blog"
        schema={SCHEMA}
      />

      <main className="px-6 md:px-12 lg:px-24 py-12">
        <div className="max-w-5xl mx-auto">

          {/* Hero */}
          <div className="text-center mb-12">
            <div className="w-20 h-20 bg-[#BDE0FE]/30 rounded-full flex items-center justify-center mx-auto mb-6">
              <BookOpen className="w-10 h-10 text-[#1A365D]" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-[#2D3748] mb-4" style={{ fontFamily: 'Nunito' }}>
              Ratgeber & Hilfe-Artikel
            </h1>
            <p className="text-lg text-[#718096] max-w-2xl mx-auto">
              Alles, was du über Mobbing, Cybermobbing und Beweissicherung wissen musst — sachlich, verständlich, kostenlos.
            </p>
          </div>

          {/* Categories */}
          <div className="flex flex-wrap gap-2 mb-10 justify-center">
            {CATEGORIES.map((cat, i) => (
              <span
                key={i}
                className={`px-4 py-1.5 rounded-full text-sm font-medium cursor-pointer transition-all ${i === 0 ? 'bg-[#2D3748] text-white' : 'bg-white border border-gray-200 text-[#718096] hover:border-gray-400'}`}
              >
                {cat}
              </span>
            ))}
          </div>

          {/* Featured */}
          <Link to={FEATURED.path} className="block mb-10 group">
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden hover:shadow-md transition-shadow">
              <div className="h-2" style={{ backgroundColor: FEATURED.color }} />
              <div className="p-8">
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-3xl">{FEATURED.emoji}</span>
                  <span className="bg-[#2D3748] text-white text-xs font-bold px-3 py-1 rounded-full">EMPFOHLEN</span>
                  <span className="text-xs px-3 py-1 rounded-full font-medium" style={{ backgroundColor: `${FEATURED.color}30`, color: '#2D3748' }}>{FEATURED.category}</span>
                  <span className="text-[#A0AEC0] text-xs flex items-center gap-1 ml-auto"><Clock className="w-3 h-3" />{FEATURED.readTime} Lesezeit</span>
                </div>
                <h2 className="text-2xl font-bold text-[#2D3748] mb-3 group-hover:text-[#1A4731] transition-colors" style={{ fontFamily: 'Nunito' }}>
                  {FEATURED.title}
                </h2>
                <p className="text-[#718096] mb-4">{FEATURED.excerpt}</p>
                <span className="inline-flex items-center gap-1 text-[#1A4731] font-bold text-sm group-hover:gap-2 transition-all">
                  Artikel lesen <ChevronRight className="w-4 h-4" />
                </span>
              </div>
            </div>
          </Link>

          {/* Articles grid */}
          <div className="grid md:grid-cols-2 gap-6 mb-12">
            {ARTICLES.map((article, i) => (
              <Link to={article.path} key={i} className="block group">
                <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden hover:shadow-md transition-shadow h-full">
                  <div className="h-1.5" style={{ backgroundColor: article.color }} />
                  <div className="p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-2xl">{article.emoji}</span>
                      <span className="text-xs px-2.5 py-1 rounded-full font-medium" style={{ backgroundColor: `${article.color}30`, color: '#2D3748' }}>{article.category}</span>
                      <span className="text-[#A0AEC0] text-xs flex items-center gap-1 ml-auto"><Clock className="w-3 h-3" />{article.readTime}</span>
                    </div>
                    <h2 className="text-base font-bold text-[#2D3748] mb-2 group-hover:text-[#1A4731] transition-colors" style={{ fontFamily: 'Nunito' }}>
                      {article.title}
                    </h2>
                    <p className="text-[#718096] text-sm mb-4 line-clamp-2">{article.excerpt}</p>
                    <span className="inline-flex items-center gap-1 text-[#1A4731] font-bold text-sm group-hover:gap-2 transition-all">
                      Lesen <ChevronRight className="w-4 h-4" />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {/* CTA */}
          <div className="bg-gradient-to-br from-[#E6FFFA] to-[#EBF8FF] rounded-2xl p-8 text-center">
            <h2 className="text-2xl font-bold text-[#2D3748] mb-3" style={{ fontFamily: 'Nunito' }}>
              Du bist betroffen? Jetzt anonym melden.
            </h2>
            <p className="text-[#718096] mb-6 max-w-xl mx-auto">
              Lesen hilft — aber wenn du selbst Mobbing erlebst, zählt jetzt handeln. Kein Name, keine Registrierung, kostenlos.
            </p>
            <Link
              to="/melden"
              className="inline-flex items-center justify-center gap-2 bg-[#A0E8AF] text-[#1A4731] font-bold px-8 py-4 rounded-full hover:bg-[#86D696] transition-all"
            >
              Jetzt Meldung einreichen
            </Link>
          </div>

        </div>
      </main>
      <Footer />
    </div>
  );
}
